import { useState } from "react";
import { COMPACT_INPUT, COMPACT_NUMBER } from "./compact";

/**
 * What a number box keeps of what was typed or pasted: digits, plus two
 * decimal places or a leading minus sign only where the box takes them.
 */
function numberText(text: string, decimals: boolean, negative: boolean) {
  const sign = negative && text.trimStart().startsWith("-") ? "-" : "";
  const [whole, ...rest] = text
    .replace(decimals ? /[^\d.]/g : /\D/g, "")
    .split(".");
  return `${sign}${whole}${rest.length ? `.${rest.join("").slice(0, 2)}` : ""}`;
}

/**
 * A number box, within its limits, that takes nothing but a number: no
 * arrows, no "4e", no "1-233". While it has the focus it shows what is being
 * typed, so 2, Backspace, 3 is 3, and its number stays as it was while it is
 * empty. Past its top it shows its top. narrow: for two digits at most.
 */
export default function NumberBox({
  value,
  min,
  max,
  onChange,
  label,
  id,
  disabled,
  step = 1,
  narrow = false,
}: {
  value: number | undefined;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
  id?: string;
  disabled?: boolean;
  step?: number;
  narrow?: boolean;
}) {
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <input
      id={id}
      type="text"
      inputMode={step < 1 ? "decimal" : "numeric"}
      aria-label={label}
      className={narrow ? `${COMPACT_INPUT} w-14` : COMPACT_NUMBER}
      disabled={disabled}
      value={typed ?? (Number.isFinite(value) ? value : "")}
      onChange={(e) => {
        const text = numberText(e.target.value, step < 1, min < 0);
        const n = Number(text);
        if (["", "-", "."].includes(text) || !Number.isFinite(n)) {
          setTyped(text);
          return;
        }
        setTyped(n > max ? String(max) : text);
        onChange(Math.min(max, Math.max(min, n)));
      }}
      onBlur={() => setTyped(null)}
    />
  );
}
